import React from "react";
import { Typography, DialogTitle, Stack, IconButton, Box } from "@mui/material";
import { Save, X, GitBranch } from "react-feather";
import ModalLayout from "./Layout";
import ModalLayoutContent from "./Layout/Content";
import ModalLayoutFooter from "./Layout/Footer";
import ModalLayoutBtn from "./Layout/Btn";

/**
 *
 * @param {{
 *  isShown: boolean;
 *  onHide: () => void;
 *  discardFiles: () => void;
 *  saveFiles: () => void;
 *  activeBranch: string;
 *  unsavedFiles: Record<string, any>
 * }} data
 */
const UnsavedFilesModal = ({
  isShown,
  onHide,
  discardFiles,
  saveFiles,
  unsavedFiles,
  saving,
}) => {
  return (
    <ModalLayout 
      isShown={isShown} 
      onHide={onHide}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          overflow: "hidden",
          background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
          border: "1px solid rgba(0,0,0,0.08)",
          transition: "all 0.3s ease-in-out"
        }
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        position={"relative"}
        padding={"16px 24px"}
        borderBottom={"1px solid rgba(0,0,0,0.05)"}
        sx={{ background: "rgba(255,237,237,0.6)" }}
      >
        <DialogTitle
          padding={"0 !important"}
          sx={{
            color: "#e63946",
            fontWeight: 800,
            fontSize: "11px",
            display: "flex",
            alignItems: "center",
            "::before": {
              content: '""',
              display: "inline-block",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#e63946",
              marginRight: "10px",
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(230, 57, 70, 0.7)" },
                "70%": { boxShadow: "0 0 0 10px rgba(230, 57, 70, 0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(230, 57, 70, 0)" }
              }
            }
          }}
        >
          <GitBranch size={14} style={{ marginRight: "6px" }} />
          Warning: Unsaved Changes
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onHide}
          sx={{
            position: "absolute",
            right: 16,
            top: 10,
            color: "#495057",
            "&:hover": {
              background: "rgba(0,0,0,0.05)",
              transform: "rotate(90deg)",
              transition: "transform 0.3s ease"
            }
          }}
        >
          <X size={16} />
        </IconButton>
      </Stack>
      <ModalLayoutContent sx={{ padding: "24px 28px" }}>
        <Typography
          sx={{ 
            fontSize: "11px", 
            color: "#4a4a4a", 
            textAlign: "center",
            lineHeight: 1.6,
            marginBottom: "16px"
          }}
        >
          You have unsaved models that will be lost if you switch branches:
          <Box
          sx={{
            background: "rgba(248,249,250,0.8)",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: "6px",
            padding: "12px 16px",
            maxHeight: "150px",
            overflowY: "auto",
            margin: "8px auto 0",
            maxWidth: "100%",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(0,0,0,0.02)",
              borderRadius: "4px"
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.1)",
              borderRadius: "4px",
              "&:hover": {
                background: "rgba(0,0,0,0.2)"
              }
            }
          }}
        >
          {Object.keys(unsavedFiles)
            .filter(file => !file.startsWith('untitled-'))
            .map(
              (file) =>
                file !== "+" && (
                  <Box 
                    key={file} 
                    sx={{ 
                      display: "block", 
                      marginBottom: "8px"
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{ 
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#1d3557",
                        background: "rgba(241,250,255,0.8)",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        border: "1px solid rgba(0,123,255,0.2)",
                        display: "inline-block"
                      }}
                    >
                      {file}
                    </Typography>
                  </Box>
                )
            )}
        </Box>

          <Typography
            component="span"
            sx={{ 
              display: "block",
              fontSize: "11px",
              fontWeight: 700,
              margin: "12px 0",
              color: "#721c24",
              background: "rgba(248,215,218,0.5)",
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px dashed rgba(220,53,69,0.3)",
              animation: "fadeIn 1.1s ease-in",
              "@keyframes fadeIn": {
                "0%": { opacity: 0, transform: "translateY(-5px)" },
                "50%": { opacity: 0.5, transform: "translateY(-5px)" },
                "100%": { opacity: 1, transform: "translateY(0)" }
              }
            }}
          >
            Do you want to save them before switching branches?
          </Typography>
        </Typography>
        
      </ModalLayoutContent>
      <ModalLayoutFooter sx={{ padding: "16px 24px", gap: "12px" }}>
        <ModalLayoutBtn 
          color="error"
          onClick={() => {
            discardFiles();
            onHide();
          }}
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s",
            padding: "8px 16px",
            background: "linear-gradient(45deg, #d32f2f, #f44336)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(211,47,47,0.25)",
            }
          }}
        >
          <X size={16} style={{ marginRight: "6px" }} /> No, discard all
        </ModalLayoutBtn>
        <ModalLayoutBtn 
          color="success" 
          onClick={() => {
            saveFiles();
            onHide();
          }} 
          disabled={saving}
          variant="contained"
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            padding: "8px 20px",
            transition: "all 0.2s",
            background: "linear-gradient(45deg, #2e7d32, #43a047)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(46,125,50,0.3)"
            },
            "&:disabled": {
              background: "#b0bec5",
              color: "#ffffff"
            }
          }}
        >
          <Save size={16} style={{ marginRight: "6px" }} /> Yes, save them
        </ModalLayoutBtn>
      </ModalLayoutFooter>
    </ModalLayout>
  );
};

export default UnsavedFilesModal;
